import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, ArrowLeft, Medal, Users, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import Avatar from "@/components/Avatar";

const METRIC_LABEL = { clout: "clout", wallet: "net worth", level: "level", lifetime_points: "Hustler Points" };
const PLACE_C = ["#FFD700", "#C0C0C0", "#CD7F32"];

export default function Contests() {
  const nav = useNavigate();
  const [contests, setContests] = useState(null);
  const [open, setOpen] = useState(null); // {contest, leaderboard, metric}

  const load = useCallback(async () => {
    try { setContests((await api.get("/contests")).data.contests); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openContest = async (id) => {
    try { setOpen((await api.get(`/contests/${id}`)).data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (open) {
    const c = open.contest;
    return (
      <div className="px-5 pt-6 pb-6" data-testid="contest-detail">
        <button onClick={() => setOpen(null)} className="flex items-center gap-1 label-xs text-slate-500 mb-2"><ArrowLeft size={14} /> All contests</button>
        <div className="rounded-2xl bg-gradient-to-br from-[#FF0055]/20 to-transparent border border-[#FF0055]/30 p-4">
          {c.sponsor && <div className="label-xs text-[#00E5FF] mb-1">Sponsored by {c.sponsor}</div>}
          <h1 className="font-display text-2xl">{c.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{c.desc}</p>
          <div className="flex items-center gap-3 mt-2 label-xs text-slate-500">
            <span className={`px-2 py-0.5 rounded-full ${c.status === "active" ? "bg-[#16A34A]/15 text-[#16A34A]" : "bg-slate-100 text-slate-500"}`}>{c.status}</span>
            <span className="flex items-center gap-1"><Trophy size={12} /> ranked by {METRIC_LABEL[open.metric] || open.metric}</span>
          </div>
          {c.prizes?.length > 0 && (
            <div className="mt-3 flex gap-2">
              {c.prizes.map((p, i) => (
                <div key={`${p.place}-${p.label}`} className="flex-1 bg-black/40 rounded-lg py-2 text-center">
                  <div className="label-xs text-slate-500">#{p.place}</div>
                  <div className="font-display" style={{ color: PLACE_C[i] || "#fff" }}>{p.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <h2 className="font-display text-lg mt-5 mb-2 flex items-center gap-2"><Medal size={16} className="text-[#FFD700]" /> {c.status === "ended" ? "Winners" : "Live standings"}</h2>
        <div className="space-y-1.5">
          {(c.status === "ended" && c.winners?.length ? c.winners : open.leaderboard).map((row) => (
            <div key={row.id} data-testid={`rank-${row.rank}`} className="flex items-center gap-3 bg-white border border-white/8 rounded-xl px-3 py-2">
              <span className="font-display w-6 text-center" style={{ color: PLACE_C[row.rank - 1] || "#71717a" }}>{row.rank}</span>
              <Avatar src={row.avatar} size={32} ring="#ffffff22" />
              <span className="flex-1 text-sm font-semibold truncate">{row.name}</span>
              <span className="label-xs text-slate-500">{(row.value ?? 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 pt-6 pb-6" data-testid="contests-page">
      <button onClick={() => nav("/room")} className="flex items-center gap-1 label-xs text-slate-500 mb-2"><ArrowLeft size={14} /> Home</button>
      <span className="label-xs text-slate-500">Skill Contests</span>
      <h1 className="font-display text-3xl mt-1">Compete & <span className="text-[#FF0055]">Win</span></h1>
      <p className="text-sm text-slate-500 mt-1">Skill-based challenges. Climb the leaderboard, win prizes. Free to enter.</p>

      {contests === null ? (
        <div className="mt-10 text-center text-slate-400">loading…</div>
      ) : contests.length === 0 ? (
        <div className="mt-12 text-center text-slate-400">
          <Trophy size={36} className="mx-auto mb-2 opacity-50" />
          No contests running right now. Check back soon! 🏆
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          {contests.map((c) => (
            <button key={c.id} data-testid={`contest-${c.id}`} onClick={() => openContest(c.id)}
              className="w-full text-left rounded-2xl border border-slate-200 bg-white p-4 active:scale-[0.99] transition-transform">
              {c.sponsor && <div className="label-xs text-[#00E5FF] mb-1">Sponsored · {c.sponsor}</div>}
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg">{c.title}</h3>
                <span className={`label-xs px-2 py-0.5 rounded-full ${c.status === "active" ? "bg-[#16A34A]/15 text-[#16A34A]" : "bg-slate-100 text-slate-500"}`}>{c.status}</span>
              </div>
              <p className="text-sm text-slate-500 mt-1 leading-tight">{c.desc}</p>
              <div className="flex items-center gap-3 mt-2 label-xs text-slate-400">
                <span className="flex items-center gap-1"><Trophy size={12} /> {METRIC_LABEL[c.metric] || c.metric}</span>
                {c.prizes?.length > 0 && <span className="flex items-center gap-1"><Medal size={12} /> {c.prizes[0]?.label} top prize</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
